import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
	Trash2,
	Loader2,
	AlertTriangle,
	CheckCircle,
	Eye,
	Users,
	Camera,
	Clock,
	FileText,
	Target,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { cn } from "@/utils/cn";
import { cleanupSimulationData, previewCleanup } from "@/services/simulation";
import { getAdminOrganizations, getOrganizationUsers } from "@/services/api";
import { useToast } from "@/components/ui/use-toast";

export function DataCleanup() {
	const { toast } = useToast();
	const [formData, setFormData] = useState({
		organizationId: "",
		userId: "",
		startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
			.toISOString()
			.split("T")[0],
		endDate: new Date().toISOString().split("T")[0],
		markedOnly: true,
	});
	const [showConfirm, setShowConfirm] = useState(false);

	// Fetch organizations
	const { data: organizations } = useQuery({
		queryKey: ["organizations-list"],
		queryFn: () => getAdminOrganizations({ limit: 100 }),
	});

	// Fetch users for selected organization
	const { data: usersData } = useQuery({
		queryKey: ["org-users", formData.organizationId],
		queryFn: () => getOrganizationUsers(formData.organizationId, { limit: 100 }),
		enabled: !!formData.organizationId,
	});

	// Preview mutation
	const previewMutation = useMutation({
		mutationFn: previewCleanup,
		onError: (error: any) => {
			toast({
				title: "Preview Failed",
				description: error.message || "Failed to preview cleanup",
				variant: "destructive",
			});
		},
	});

	// Cleanup mutation
	const cleanupMutation = useMutation({
		mutationFn: cleanupSimulationData,
		onSuccess: (data) => {
			toast({
				title: "Cleanup Complete",
				description: `Deleted ${data.sessionsDeleted} sessions and related data`,
			});
			setShowConfirm(false);
			previewMutation.reset();
		},
		onError: (error: any) => {
			toast({
				title: "Cleanup Failed",
				description: error.message || "Failed to cleanup data",
				variant: "destructive",
			});
		},
	});

	const handlePreview = () => {
		if (!formData.organizationId) {
			toast({
				title: "Error",
				description: "Please select an organization",
				variant: "destructive",
			});
			return;
		}
		previewMutation.mutate(formData);
	};

	const handleCleanup = () => {
		cleanupMutation.mutate(formData);
	};

	const preview = previewMutation.data;
	const result = cleanupMutation.data;

	const totalItems = preview
		? preview.sessionsDeleted +
			preview.screenshotsDeleted +
			preview.appUsageDeleted +
			preview.idleEventsDeleted +
			preview.summariesDeleted +
			preview.kpiDeleted
		: 0;

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
						<Trash2 className="w-6 h-6" />
						Simulation Data Cleanup
					</h1>
					<p className="text-gray-500 mt-1">
						Remove simulation data from the database
					</p>
				</div>
			</div>

			{/* Warning Banner */}
			<div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
				<AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
				<div>
					<p className="font-medium text-amber-900">
						Warning: This will permanently delete monitoring data!
					</p>
					<p className="text-sm text-amber-700 mt-1">
						Always preview before deleting. Use "Marked Only" to only delete
						simulation-generated data (appVersion 1.2.0).
					</p>
				</div>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* Configuration */}
				<Card>
					<CardHeader>
						<CardTitle>Cleanup Configuration</CardTitle>
					</CardHeader>
					<CardContent className="space-y-6">
						{/* Organization */}
						<div>
							<Label>Organization</Label>
							<Select
								value={formData.organizationId}
								onValueChange={(value) =>
									setFormData({ ...formData, organizationId: value, userId: "" })
								}
							>
								<SelectTrigger className="mt-2">
									<SelectValue placeholder="Select an organization" />
								</SelectTrigger>
								<SelectContent>
									{organizations?.organizations?.map((org) => (
										<SelectItem key={org._id} value={org._id}>
											{org.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{/* User */}
						<div>
							<Label>User (optional)</Label>
							<Select
								value={formData.userId || "all"}
								onValueChange={(value) =>
									setFormData({ ...formData, userId: value === "all" ? "" : value })
								}
								disabled={!formData.organizationId}
							>
								<SelectTrigger className="mt-2">
									<SelectValue placeholder="All users" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">All Users</SelectItem>
									{usersData?.users?.map((user) => (
										<SelectItem key={user._id} value={user._id}>
											{user.email}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{/* Date Range */}
						<div className="grid grid-cols-2 gap-4">
							<div>
								<Label>Start Date</Label>
								<input
									type="date"
									value={formData.startDate}
									onChange={(e) =>
										setFormData({ ...formData, startDate: e.target.value })
									}
									className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
								/>
							</div>
							<div>
								<Label>End Date</Label>
								<input
									type="date"
									value={formData.endDate}
									onChange={(e) =>
										setFormData({ ...formData, endDate: e.target.value })
									}
									className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
								/>
							</div>
						</div>

						{/* Marked Only Toggle */}
						<div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
							<div>
								<Label className="text-blue-900">Only Marked Simulation Data</Label>
								<p className="text-sm text-blue-700">
									Delete only data with appVersion="1.2.0"
								</p>
							</div>
							<Switch
								checked={formData.markedOnly}
								onCheckedChange={(checked) =>
									setFormData({ ...formData, markedOnly: checked })
								}
							/>
						</div>

						{/* Preview Button */}
						<Button
							className="w-full"
							variant="outline"
							size="lg"
							onClick={handlePreview}
							disabled={previewMutation.isPending || !formData.organizationId}
						>
							{previewMutation.isPending ? (
								<Loader2 className="w-4 h-4 mr-2 animate-spin" />
							) : (
								<Eye className="w-4 h-4 mr-2" />
							)}
							Preview Changes
						</Button>
					</CardContent>
				</Card>

				{/* Preview / Results */}
				<Card>
					<CardHeader>
						<CardTitle>
							{result ? "Cleanup Results" : "Preview"}
						</CardTitle>
					</CardHeader>
					<CardContent>
						{result ? (
							<div className="space-y-6">
								{/* Success */}
								<div className="p-6 bg-green-50 rounded-lg text-center">
									<CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
									<p className="font-medium text-lg text-green-700">
										Cleanup Complete
									</p>
								</div>

								{/* Deleted Counts */}
								<div className="grid grid-cols-2 gap-3">
									<div className="p-3 bg-gray-50 rounded-lg">
										<div className="flex items-center gap-2 mb-1">
											<Users className="w-4 h-4 text-blue-500" />
											<span className="text-xs text-gray-500">Sessions</span>
										</div>
										<p className="text-xl font-bold text-red-600">
											-{result.sessionsDeleted}
										</p>
									</div>
									<div className="p-3 bg-gray-50 rounded-lg">
										<div className="flex items-center gap-2 mb-1">
											<Camera className="w-4 h-4 text-purple-500" />
											<span className="text-xs text-gray-500">Screenshots</span>
										</div>
										<p className="text-xl font-bold text-red-600">
											-{result.screenshotsDeleted}
										</p>
									</div>
									<div className="p-3 bg-gray-50 rounded-lg">
										<div className="flex items-center gap-2 mb-1">
											<Clock className="w-4 h-4 text-green-500" />
											<span className="text-xs text-gray-500">App Usage</span>
										</div>
										<p className="text-xl font-bold text-red-600">
											-{result.appUsageDeleted}
										</p>
									</div>
									<div className="p-3 bg-gray-50 rounded-lg">
										<div className="flex items-center gap-2 mb-1">
											<FileText className="w-4 h-4 text-amber-500" />
											<span className="text-xs text-gray-500">Summaries</span>
										</div>
										<p className="text-xl font-bold text-red-600">
											-{result.summariesDeleted}
										</p>
									</div>
									<div className="p-3 bg-gray-50 rounded-lg">
										<div className="flex items-center gap-2 mb-1">
											<Target className="w-4 h-4 text-indigo-500" />
											<span className="text-xs text-gray-500">KPI Records</span>
										</div>
										<p className="text-xl font-bold text-red-600">
											-{result.kpiDeleted}
										</p>
									</div>
									<div className="p-3 bg-gray-50 rounded-lg">
										<div className="flex items-center gap-2 mb-1">
											<Clock className="w-4 h-4 text-orange-500" />
											<span className="text-xs text-gray-500">Idle Events</span>
										</div>
										<p className="text-xl font-bold text-red-600">
											-{result.idleEventsDeleted}
										</p>
									</div>
								</div>
							</div>
						) : preview ? (
							<div className="space-y-6">
								{/* Preview Header */}
								<div className={cn(
									"p-4 rounded-lg text-center",
									totalItems > 0 ? "bg-amber-50" : "bg-gray-50"
								)}>
									{totalItems > 0 ? (
										<>
											<AlertTriangle className="w-10 h-10 mx-auto mb-2 text-amber-500" />
											<p className="font-medium text-amber-900">
												{totalItems.toLocaleString()} items will be deleted
											</p>
										</>
									) : (
										<>
											<CheckCircle className="w-10 h-10 mx-auto mb-2 text-gray-400" />
											<p className="font-medium text-gray-600">
												No matching data found
											</p>
										</>
									)}
								</div>

								{/* Preview Counts */}
								<div className="space-y-2">
									<div className="flex items-center justify-between p-3 bg-gray-50 rounded">
										<div className="flex items-center gap-2">
											<Users className="w-4 h-4 text-blue-500" />
											<span>Sessions to delete</span>
										</div>
										<Badge variant="destructive">{preview.sessionsDeleted}</Badge>
									</div>
									<div className="flex items-center justify-between p-3 bg-gray-50 rounded">
										<div className="flex items-center gap-2">
											<Camera className="w-4 h-4 text-purple-500" />
											<span>Screenshots to delete</span>
										</div>
										<Badge variant="destructive">{preview.screenshotsDeleted}</Badge>
									</div>
									<div className="flex items-center justify-between p-3 bg-gray-50 rounded">
										<div className="flex items-center gap-2">
											<Clock className="w-4 h-4 text-green-500" />
											<span>App usage records</span>
										</div>
										<Badge variant="destructive">{preview.appUsageDeleted}</Badge>
									</div>
									<div className="flex items-center justify-between p-3 bg-gray-50 rounded">
										<div className="flex items-center gap-2">
											<FileText className="w-4 h-4 text-amber-500" />
											<span>Session summaries</span>
										</div>
										<Badge variant="destructive">{preview.summariesDeleted}</Badge>
									</div>
									<div className="flex items-center justify-between p-3 bg-gray-50 rounded">
										<div className="flex items-center gap-2">
											<Target className="w-4 h-4 text-indigo-500" />
											<span>KPI records</span>
										</div>
										<Badge variant="destructive">{preview.kpiDeleted}</Badge>
									</div>
								</div>

								{/* Confirm Dialog */}
								{totalItems > 0 && (
									<div className="border-t pt-4 space-y-3">
										{showConfirm ? (
											<>
												<p className="text-sm text-red-600 text-center font-medium">
													Are you sure? This action cannot be undone!
												</p>
												<div className="flex gap-2">
													<Button
														variant="outline"
														className="flex-1"
														onClick={() => setShowConfirm(false)}
													>
														Cancel
													</Button>
													<Button
														variant="destructive"
														className="flex-1"
														onClick={handleCleanup}
														disabled={cleanupMutation.isPending}
													>
														{cleanupMutation.isPending ? (
															<Loader2 className="w-4 h-4 mr-2 animate-spin" />
														) : (
															<Trash2 className="w-4 h-4 mr-2" />
														)}
														Confirm Delete
													</Button>
												</div>
											</>
										) : (
											<Button
												variant="destructive"
												className="w-full"
												onClick={() => setShowConfirm(true)}
											>
												<Trash2 className="w-4 h-4 mr-2" />
												Delete Data
											</Button>
										)}
									</div>
								)}
							</div>
						) : (
							<div className="text-center py-12 text-gray-500">
								<Eye className="w-12 h-12 mx-auto mb-4 opacity-50" />
								<p>Configure filters and click Preview to see what will be deleted</p>
							</div>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
